import { Link } from "react-router-dom";
import { ArrowUpRight, Mic, Users, Building2, Sparkles, Radio, Download } from "lucide-react";
import { FOUNDER_SPEAKING, FOUNDER_SEATED, SPEAKER_SHEET_URL, EXTERNAL_REQUEST_URL } from "@/lib/constants";

const keynotes = [
  {
    title: "Impossible Is Where Your Genius Begins",
    body: "Break through limiting beliefs and unlock the actionable genius in every student and educator.",
    audience: "Assemblies · Faculty PD",
  },
  {
    title: "Make Great Decisions Faster",
    body: "A repeatable framework — by whose authority, by what measure — for college, career, and leadership.",
    audience: "Students · Leaders",
  },
  {
    title: "Unbox Your Leadership",
    body: "Faculty and educational leaders discover their Genius Leadership Archetype™ and learn to lead from it.",
    audience: "Faculty & Staff",
  },
  {
    title: "Unabashedly Me",
    body: "Confidence and authenticity that help young people show up fully and choose boldly.",
    audience: "Student Bodies",
  },
  {
    title: "The Genius Leader's Blueprint",
    body: "Redefine success and ignite change in private schools — the campus-leadership deep-dive keynote.",
    audience: "Heads of School · Boards",
  },
];

const economicsTracks = [
  { title: "Navigating Financial Windfalls", body: "How institutions and individuals can steward sudden capital, campaigns, and windfalls with clarity." },
  { title: "Profit from Your IP", body: "Turning institutional expertise, curriculum and content into scalable intellectual-property revenue." },
];

const formats = [
  { icon: Mic, title: "Keynote", body: "30 to 90 minutes. Live or virtual. Opening general sessions, conferences, association meetings, board retreats." },
  { icon: Sparkles, title: "Workshop & \"Play\"shop", body: "Half-day to multi-day. Interactive formats that establish immediate practices with your team." },
  { icon: Radio, title: "Virtual Training", body: "Live and interactive. Perfect for distributed teams, faculty PD across campuses, and hybrid events." },
  { icon: Users, title: "Assembly & Faculty PD", body: "Purpose-built experiences for students and staff on the same campus, same day." },
];

const testimonials = [
  { q: "Thank you for being the coolest teacher ever — I've used what I learned with you in my life.", n: "E. Wallace", r: "Student" },
  { q: "Well worth your time. I highly recommend her for your audiences.", n: "Tiffany O.", r: "CEO" },
  { q: "I thoroughly enjoyed your presentation — and your instinct to present without slides.", n: "T. Barry", r: "CEO" },
];

const asSeenOn = ["CFO Magazine", "Global Woman", "Miami Made", "BizTV Shows", "AUREA", "Carnegie Hall"];

export default function Speaking() {
  return (
    <div data-testid="page-speaking">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0F4C5C] text-[#FDFBF7]">
        <div className="absolute inset-0">
          <img src={FOUNDER_SPEAKING} alt="" className="w-full h-full object-contain object-right opacity-95" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F4C5C] via-[#0F4C5C]/95 to-[#0F4C5C]/50" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-24 md:pt-36 md:pb-32">
          <div className="max-w-3xl">
            <div className="overline text-[#E3B23C]">Speaking with Dr. Crystal Gifford</div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] mt-4">
              Make great decisions <em className="italic text-[#E3B23C]">faster.</em>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#FDFBF7]/90 leading-relaxed max-w-2xl">
              A magnetic speaker who turns decision science and economics into energy, clarity and action — from intimate rooms to audiences in the thousands, on campus and on the world's biggest stages.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={EXTERNAL_REQUEST_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="speaking-cta-book"
                className="btn-primary bg-[#E3B23C] border-[#E3B23C] text-[#22223B] hover:bg-[#d1a12f]"
              >
                Request Dr. Crystal <ArrowUpRight size={16} />
              </a>
              <a
                href={SPEAKER_SHEET_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="speaking-cta-sheet"
                className="btn-secondary text-[#FDFBF7] border-[#FDFBF7]/60 hover:bg-white/10"
              >
                <Download size={16} /> Download Speaker Sheet
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CREDENTIAL STRIP */}
      <section className="bg-[#F0EDE6] py-10 md:py-14 border-y border-black/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="overline text-[#6B705C] mb-6 text-center">As seen on & on stage at</div>
          <div className="flex flex-wrap gap-x-10 gap-y-4 justify-center items-center text-[#22223B]/70">
            {asSeenOn.map((n) => (
              <span key={n} className="font-serif-display text-lg md:text-xl italic">{n}</span>
            ))}
          </div>
        </div>
      </section>

      {/* MASTER OF CEREMONIES — Featured spotlight */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="relative overflow-hidden rounded-2xl bg-[#0F4C5C] text-[#FDFBF7] grain">
            <div className="relative grid md:grid-cols-12 gap-10 p-10 md:p-16 items-center">
              <div className="md:col-span-7">
                <div className="overline text-[#E3B23C] flex items-center gap-2">
                  <Mic size={14} /> Now Booking · Master of Ceremonies
                </div>
                <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.05] mt-4">
                  The MC who keeps the <em className="italic text-[#E3B23C]">room alive.</em>
                </h2>
                <p className="mt-6 text-lg text-[#FDFBF7]/85 leading-relaxed max-w-2xl">
                  Conferences, galas, awards nights, faculty-and-staff assemblies, association meetings — Dr. Crystal is the host who reads the room, protects your agenda, and keeps energy and attention high from opening remarks to closing applause. Speaking since 1999. Comfortable in front of eight people or eight thousand.
                </p>
                <ul className="mt-8 grid sm:grid-cols-2 gap-y-3 gap-x-6">
                  {[
                    "Opens, transitions and closes with intent",
                    "Custom scripting to your run-of-show",
                    "Interviews sponsors, honorees and panels",
                    "Owns the stage without upstaging it",
                    "In-person, virtual, or hybrid",
                    "Can pair MC role with a keynote slot",
                  ].map((p) => (
                    <li key={p} className="flex gap-3 text-[15px] text-[#FDFBF7]/85">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#E3B23C] shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                <div className="mt-10 flex flex-wrap gap-3">
                  <a
                    href={EXTERNAL_REQUEST_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="mc-primary-cta"
                    className="btn-primary bg-[#E3B23C] border-[#E3B23C] text-[#22223B] hover:bg-[#d1a12f]"
                  >
                    Request Her as Your MC <ArrowUpRight size={16} />
                  </a>
                  <a
                    href={EXTERNAL_REQUEST_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary text-[#FDFBF7] border-[#FDFBF7]/60 hover:bg-white/10"
                  >
                    Ask About MC + Keynote Package
                  </a>
                </div>
              </div>
              <div className="md:col-span-5">
                <div className="aspect-[4/5] rounded-xl overflow-hidden bg-white/5 border border-white/10 flex items-end justify-center">
                  <img src={FOUNDER_SPEAKING} alt="Dr. Crystal Gifford hosting on stage" className="w-full h-full object-contain object-bottom" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIGNATURE KEYNOTES */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-8 mb-14">
            <div className="md:col-span-7">
              <div className="overline">Signature keynotes</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
                Talks for school and leadership audiences.
              </h2>
            </div>
            <p className="md:col-span-5 text-[#22223B]/75 leading-relaxed">
              Every keynote is customized to your audience and event. Money and economics tracks also available — <em className="italic">Navigating Financial Windfalls</em> and <em className="italic">Profit from Your IP.</em>
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {keynotes.map((t) => (
              <div key={t.title} className="card-soft p-8">
                <div className="overline text-[#0F4C5C]">Best for {t.audience}</div>
                <h3 className="font-serif-display text-2xl md:text-3xl leading-snug mt-3">{t.title}</h3>
                <p className="mt-4 text-[15px] text-[#22223B]/75 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {economicsTracks.map((t) => (
              <div key={t.title} className="p-6 rounded-lg border border-dashed border-[#0F4C5C]/25 bg-[#F0EDE6]/50">
                <div className="overline text-[#0F4C5C]">Economics & Money Track</div>
                <h4 className="font-serif-display text-xl mt-2">{t.title}</h4>
                <p className="mt-2 text-sm text-[#22223B]/75">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-lg overflow-hidden">
              <img src={FOUNDER_SEATED} alt="Dr. Crystal Gifford" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="overline">Formats & experiences</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              Choose your mix.
            </h2>
            <div className="mt-8 space-y-4">
              {formats.map((f) => (
                <div key={f.title} className="card-soft p-6 flex gap-5 items-start">
                  <f.icon size={24} strokeWidth={1.4} className="text-[#0F4C5C] mt-1" />
                  <div>
                    <h3 className="font-serif-display text-xl">{f.title}</h3>
                    <p className="mt-2 text-[15px] text-[#22223B]/75">{f.body}</p>
                  </div>
                </div>
              ))}
              <div className="p-6 rounded-lg border border-[#0F4C5C]/20 bg-[#FDFBF7]/60 flex gap-5 items-start">
                <Building2 size={24} strokeWidth={1.4} className="text-[#0F4C5C] mt-1" />
                <div>
                  <h3 className="font-serif-display text-xl">Master of Ceremonies</h3>
                  <p className="mt-2 text-[15px] text-[#22223B]/75">Host your full event, awards night or conference. Custom scripting, sponsor interviews, transitions — energy from open to close.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <div className="overline">What they're saying</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">Field notes from the room.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {testimonials.map((t, i) => (
              <figure key={i} className="card-soft p-8">
                <blockquote className="text-[17px] leading-relaxed text-[#22223B]/85">&ldquo;{t.q}&rdquo;</blockquote>
                <figcaption className="mt-6 pt-6 border-t border-black/5">
                  <div className="font-serif-display text-lg">{t.n}</div>
                  <div className="text-sm text-[#6B705C]">{t.r}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="section-pad bg-[#0F4C5C] text-[#FDFBF7] grain relative overflow-hidden">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <div className="overline text-[#E3B23C]">Bring her to your stage</div>
          <h2 className="font-serif-display text-4xl md:text-6xl mt-4 leading-tight">
            Request Dr. Crystal.
          </h2>
          <p className="mt-6 text-[#FDFBF7]/80 max-w-xl mx-auto">
            Keynote, workshop, faculty PD, student assembly, or Master of Ceremonies for your full event. Tell us what you're planning and we'll build the right experience.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a
              href={EXTERNAL_REQUEST_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="speaking-bottom-cta"
              className="btn-primary bg-[#E3B23C] border-[#E3B23C] text-[#22223B] hover:bg-[#d1a12f]"
            >
              Request Dr. Crystal <ArrowUpRight size={16} />
            </a>
            <a
              href={SPEAKER_SHEET_URL}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary text-[#FDFBF7] border-[#FDFBF7]/60 hover:bg-white/10"
            >
              <Download size={16} /> Speaker Sheet
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
